import type { ChatMessages } from "@storytell/cp-client";
import { TbAlertCircle, TbPhoto } from "solid-icons/tb";
import { Show } from "solid-js";
import { ImageWithSignedURL } from "../ImageWithSignedURL";

interface ImageResultsRendererProps {
  imageResults: ChatMessages.ImageResult;
}

/**
 * Renders generated images with metadata
 */
export const ImageResultsRenderer = (props: ImageResultsRendererProps) => {
  const storageLocation = () => props.imageResults.storageLocation;
  const metadata = () => props.imageResults.metadata;

  return (
    <div
      data-renderer="image-results-renderer"
      class="text-base bg-panel pl-5 pr-5 -ml-2 pt-3 pb-3 rounded-xl border border-primary-border"
    >
      <Show
        when={storageLocation()}
        fallback={<p class="text-sm text-primary-text italic">Image data not available</p>}
      >
        <div class="space-y-3">
          <ImageWithSignedURL
            storageLocation={storageLocation()!}
            metadata={{
              prompt: metadata()?.prompt,
              revisedPrompt: metadata()?.revisedPrompt,
              model: metadata()?.model,
            }}
          />

          <div class="flex items-start gap-2 p-3 bg-inset rounded-lg text-xs text-secondary-text">
            <TbAlertCircle class="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              Image generation is a beta feature and is more prone to errors like misspellings in charts and graphs.
              Make sure to double check the underlying data before using.
            </span>
          </div>

          <Show when={metadata() && (metadata()?.model || metadata()?.revisedPrompt)}>
            <div class="space-y-2 text-xs text-secondary-text">
              <Show when={metadata()?.revisedPrompt}>
                <div class="space-y-1">
                  <div class="font-medium text-tertiary-text">Revised prompt:</div>
                  <div class="text-secondary-text italic">{metadata()?.revisedPrompt}</div>
                </div>
              </Show>

              <Show when={metadata()?.aspectRatio}>
                <div class="flex items-center gap-2">
                  <span class="font-medium text-tertiary-text">Aspect Ratio:</span>
                  <span>{metadata()?.aspectRatio}</span>
                </div>
              </Show>
            </div>
          </Show>
        </div>
      </Show>
    </div>
  );
};
